import { zodResolver } from "@hookform/resolvers/zod";
import { Download, Info, Loader } from "lucide-react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useExportOwnersMutation } from "@/data/mutations";
import { useGetSKUsForSKUSelectionQuery } from "@/data/queries";
import { canExportOwners } from "@/lib/permissions";
import useStore from "@/state";

const exportOwnersFormSchema = z.object({
  terms: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
  skuId: z.string().min(1, "You must select a SKU"),
});

export const Owners = () => {
  const [searchParams] = useSearchParams();
  const form = useForm<z.infer<typeof exportOwnersFormSchema>>({
    resolver: zodResolver(exportOwnersFormSchema),
    defaultValues: {
      terms: false,
      skuId: searchParams.get("templateId") ?? undefined,
    },
  });
  const exportMutation = useExportOwnersMutation();
  const { data, isLoading } = useGetSKUsForSKUSelectionQuery();
  const { session } = useStore();

  const onSubmit = async (values: z.infer<typeof exportOwnersFormSchema>) => {
    await exportMutation.mutateAsync(values.skuId);
  };

  if (isLoading) {
    return <div />;
  }

  if (!session) return null;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
            Owners
          </h2>

          <Alert className="mt-6">
            <Info className="h-4 w-4" />
            <AlertTitle className="font-medium text-base">
              Owners are end-consumers who have completed the following steps:
            </AlertTitle>
            <AlertDescription className="py-4">
              <ol className="list-none py-2">
                <li>1. Created an account through Legitimate</li>
                <li>2. Verified a tagged product</li>
                <li>3. Successfully claimed a Digital Product</li>
              </ol>
            </AlertDescription>
            <AlertTitle>
              The CSV export of Owners will contain the following information:
            </AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-4 py-2">
                <li>SKU ID</li>
                <li>Tag ID</li>
                <li>Serial Number</li>
                <li>Owner ID</li>
                <li>Owner Email</li>
                <li>Marketing Consent for Email Captured</li>
              </ul>
              <i>
                Owners who did not consent for marketing will not have their
                emails included in the export.
              </i>
            </AlertDescription>
          </Alert>

          <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-6">
            Download CSV
          </h3>

          {canExportOwners(session?.permissions) ? (
            <>
              <FormField
                control={form.control}
                name="skuId"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value?.toString()}
                    >
                      <SelectTrigger className="w-[317px] mt-6">
                        <SelectValue placeholder="Select a SKU" />
                      </SelectTrigger>
                      <SelectContent>
                        {data?.templates?.map((sku) => (
                          <SelectItem key={sku.id} value={sku.id.toString()}>
                            {sku.id} - {sku.name}
                          </SelectItem>
                        )) ?? []}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="terms"
                render={({ field }) => (
                  <FormItem className="flex space-x-2 mt-6">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="grid gap-1.5 leading-none">
                      <FormLabel>Terms and Conditions</FormLabel>
                      <FormDescription>
                        By downloading a CSV export of the ownership information
                        for your Legitimate Tags, you agree to the terms and
                        conditions set forth by Legitimate, Inc. and agree to
                        not hold Legitimate responsible for any misuse of the
                        data contained in the export.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              <div className="flex gap-2 mt-6">
                <Button
                  type="submit"
                  disabled={!form.formState.isValid || exportMutation.isPending}
                >
                  {exportMutation.isPending && (
                    <Loader className="animate-spin" />
                  )}
                  {!exportMutation.isPending && <Download />}
                  Download CSV
                </Button>
              </div>
            </>
          ) : (
            <p className="mt-6 text-muted-foreground text-sm">
              Your role does not have permission to download the CSV of owners.
              To change your permissions, contact Legitimate or reach out to
              your account administrator to update your role.
            </p>
          )}
        </div>
      </form>
    </Form>
  );
};
